import React, { useState } from "react";
import ConsultationForm from "./Consultation"; // adjust path if your pages folder is elsewhere
import Diagnosics1 from "../assets/images/Diagnosics/Diagnosics-1.webp";
import Diagnosics2 from "../assets/images/Diagnosics/Diagnosics-2.png";
import Diagnosics3 from "../assets/images/Diagnosics/Diagnosics-3.webp";
import Diagnosics4 from "../assets/images/Diagnosics/Diagnosics-4.jpg";
import Diagnosics5 from "../assets/images/Diagnosics/Diagnosics-5.jpeg";
import Diagnosics6 from "../assets/images/Diagnosics/Diagnosics-6.jpeg";
import Diagnosics7 from "../assets/images/Diagnosics/Diagnosics-7.jpg";
import Diagnosics8 from "../assets/images/Diagnosics/Diagnosics-8.jpeg";
import Diagnosics9 from "../assets/images/Diagnosics/Diagnosics-9.jpg";
import SEO from "../components/SEO";

/**
 * Diagnostic Room Designs — landing page
 * Same design system as OTSuiteDesigns.jsx — black/white/grey/ash palette,
 * spec-line cards (Zone / Size / Modality), "Get Free Quote" opens the shared Consultation modal.
 */

const FONT_IMPORT_URL =
  "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap";

const tokens = {
  ink: "#343F86",
  bg: "#ECEEF6",
  card: "#FFFFFF",
  teal: "#3A4060",
  tealDark: "#262E63",
  amber: "#8A8F9C",
  blue: "#5B6072",
  hairline: "#CDD1E0",
  sub: "#5B6072",
};

const diagnosticRooms = [
  {
    name: "X-Ray Diagnostic Room",
    zone: "Ground Floor",
    size: "24 sq.m",
    spec: "Digital X-Ray",
    tag: "Radiology",
    note: "Lead-lined walls, motorized table, control room with leaded glass viewing window.",
    image: Diagnosics1, // 👈 add your image path/URL here
  },
  {
    name: "CT Scan Suite",
    zone: "Ground Floor",
    size: "40 sq.m",
    spec: "128-Slice CT",
    tag: "CT Scan",
    note: "Wide-bore gantry bay, contrast injector room, radiation-shielded control console.",
    image: Diagnosics2,
  },
  {
    name: "MRI Diagnostic Suite",
    zone: "Ground Floor",
    size: "55 sq.m",
    spec: "1.5T MRI",
    tag: "MRI",
    note: "RF-shielded room, cryogen quench vent, ferrous-free zone with metal detection at entry.",
    image: Diagnosics3,
  },
  {
    name: "Ultrasound & Sonography Room",
    zone: "Ground Floor",
    size: "16 sq.m",
    spec: "Color Doppler",
    tag: "Ultrasound",
    note: "Dimmable lighting, private changing cubicle, ergonomic exam couch with adjustable height.",
    image: Diagnosics4,
  },
  {
    name: "Mammography Diagnostic Room",
    zone: "Ground Floor",
    size: "18 sq.m",
    spec: "Digital Mammo",
    tag: "Mammography",
    note: "Dedicated patient privacy lobby, soft lighting, lead-shielded compression unit bay.",
    image: Diagnosics5,
  },
  {
    name: "ECG & Cardiac Diagnostics Room",
    zone: "Ground Floor",
    size: "14 sq.m",
    spec: "ECG / Echo",
    tag: "Cardiac",
    note: "Quiet acoustic panels, reclining exam table, dedicated echo machine bay.",
    image: Diagnosics6,
  },
  {
    name: "Endoscopy Diagnostic Suite",
    zone: "Ground Floor",
    size: "26 sq.m",
    spec: "Endoscopy",
    tag: "Endoscopy",
    note: "Scope-cleaning anteroom, recovery bay adjacency, anesthesia-ready gas points.",
    image: Diagnosics7,
  },
  {
    name: "Bone Densitometry (DEXA) Room",
    zone: "Ground Floor",
    size: "15 sq.m",
    spec: "DEXA Scan",
    tag: "DEXA",
    note: "Low-dose imaging bay, patient prep area, compact footprint for OPD integration.",
    image: Diagnosics8,
  },
  {
    name: "Multi-Modality Diagnostic Block",
    zone: "Ground Floor",
    size: "150 sq.m",
    spec: "X-Ray + CT + USG",
    tag: "Multi-Modality",
    note: "Shared waiting lounge, central reporting room, modular imaging bays under one roof.",
    image: Diagnosics9,
  },
];

function SpecLine({ zone, size, spec }) {
  return (
    <div
      style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 11.5,
        letterSpacing: 0.3,
        color: tokens.sub,
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
      }}
    >
      <span>ZONE&nbsp;<b style={{ color: tokens.teal }}>{zone}</b></span>
      <span>·</span>
      <span>SIZE&nbsp;<b style={{ color: tokens.ink }}>{size}</b></span>
      <span>·</span>
      <span>{spec}</span>
    </div>
  );
}

function DiagnosticCard({ item, onGetQuote }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        background: tokens.card,
        borderRadius: 4,
        overflow: "hidden",
        border: `1px solid ${tokens.hairline}`,
        boxShadow: hover
          ? "0 14px 28px -14px rgba(14,124,123,0.35)"
          : "0 2px 6px -2px rgba(43,58,66,0.08)",
        transform: hover ? "translateY(-3px)" : "none",
        transition: "all 0.25s ease",
      }}
    >
      {/* corner bracket signature */}
      <div
        style={{
          position: "absolute", top: 10, left: 10, width: 22, height: 22,
          borderTop: `2px solid ${tokens.teal}`, borderLeft: `2px solid ${tokens.teal}`,
          zIndex: 2,
        }}
      />
      <div style={{ height: 168, position: "relative", background: tokens.tealDark }}>
        {item.image ? (
          <img
            src={item.image}
            alt={item.name}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <div
            style={{
              width: "100%", height: "100%", display: "flex",
              alignItems: "center", justifyContent: "center",
              color: "rgba(255,255,255,0.55)",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, letterSpacing: 0.5,
            }}
          >
            ADD IMAGE
          </div>
        )}
        {/* tag chip */}
        <span
          style={{
            position: "absolute", bottom: 10, right: 12,
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
            color: "#fff", background: "rgba(0,0,0,0.45)",
            padding: "2px 8px", borderRadius: 2,
          }}
        >
          {item.tag.toUpperCase()}
        </span>
      </div>
      <div style={{ padding: "16px 16px 18px" }}>
        <h3
          style={{
            fontFamily: "'Montserrat', 'Inter', sans-serif", fontWeight: 600, fontSize: 17.5,
            color: tokens.ink, margin: "0 0 6px", lineHeight: 1.25,
          }}
        >
          {item.name}
        </h3>
        <p style={{ fontSize: 13, color: tokens.sub, margin: "0 0 10px", lineHeight: 1.45 }}>
          {item.note}
        </p>
        <SpecLine zone={item.zone} size={item.size} spec={item.spec} />
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button
            onClick={() => onGetQuote(item)}
            style={{
              flex: 1, padding: "9px 10px", fontSize: 12.5, fontWeight: 600,
              fontFamily: "'Montserrat', 'Inter', sans-serif", letterSpacing: 0.2,
              background: tokens.amber, color: tokens.ink,
              border: "none", borderRadius: 3, cursor: "pointer",
            }}
          >
            Get Free Quote
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DiagnosticRoomDesigns() {
  const [showConsultation, setShowConsultation] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const handleGetQuote = (item) => {
    setSelectedItem(item);
    setShowConsultation(true);
  };

  return (
     <>
    <SEO/>
    <div
      style={{
        background: tokens.bg,
        color: tokens.ink,
        fontFamily: "'Inter', 'Montserrat', sans-serif",
        minHeight: "100vh",
      }}
    >
      <style>{`@import url('${FONT_IMPORT_URL}');
        * { box-sizing: border-box; }
        @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
        button:focus-visible, a:focus-visible { outline: 2px solid ${tokens.teal}; outline-offset: 2px; }
        .diag-grid-3x3 { grid-template-columns: repeat(3, 1fr); }
        @media (max-width: 900px) { .diag-grid-3x3 { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 600px) { .diag-grid-3x3 { grid-template-columns: 1fr; } }
        .diag-modal-overlay {
          position: fixed; inset: 0; z-index: 2000;
          background: rgba(0, 0, 0, 0.65);
          backdrop-filter: blur(5px);
          display: flex; align-items: center; justify-content: center;
          padding: 1rem;
        }
        .diag-modal-inner {
          position: relative; width: 100%; max-width: 680px;
          max-height: 90vh; overflow-y: auto; border-radius: 18px;
        }
        .diag-modal-close {
          position: absolute; top: 14px; right: 16px; z-index: 10;
          background: #262E63; border: none; color: #fff;
          width: 32px; height: 32px; border-radius: 50%;
          font-size: 20px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          line-height: 1;
        }
      `}</style>

      {/* spacer so the fixed/sticky page navbar in the host app doesn't overlap the grid */}
      <div style={{ height: 32 }} />

      {/* GRID */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "26px 24px 10px" }}>
        <div
          className="diag-grid-3x3"
          style={{
            display: "grid",
            gap: 22,
            marginTop: 70,
          }}
        >
          {diagnosticRooms.map((item, i) => (
            <DiagnosticCard key={i} item={item} onGetQuote={handleGetQuote} />
          ))}
        </div>
      </section>

      {/* Consultation Modal — opens on "Get Free Quote" click */}
      {showConsultation && (
        <div
          className="diag-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowConsultation(false); }}
        >
          <div className="diag-modal-inner">
            <button
              className="diag-modal-close"
              onClick={() => setShowConsultation(false)}
              aria-label="Close consultation form"
            >
              ×
            </button>
            <ConsultationForm suite={selectedItem} />
          </div>
        </div>
      )}
    </div>
    </>
  );
}